import { useCallback, useEffect, useState } from 'react'
import { currentLocaleTag } from '../../lib/formatting'
import {
  AlertTriangle,
  Check,
  KeyRound,
  Loader2,
  Monitor,
  ShieldCheck,
  ShieldOff,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import { useAuth } from '../../contexts/AuthContext'

interface MfaStatus {
  enabled: boolean
  method: string | null
  lastVerifiedAt: string | null
  recoveryCodesRemaining: number
}

interface SessionItem {
  id: string
  ipCreated: string | null
  ipLastSeen: string | null
  userAgent: string | null
  createdAt: string
  lastSeenAt: string
  idleExpiresAt: string
  absoluteExpiresAt: string
  isCurrent: boolean
}

export default function SecuritySettingsPage() {
  const { t } = useTranslation('settings')
  const auth = useAuth()
  const [mfa, setMfa] = useState<MfaStatus | null>(null)
  const [sessions, setSessions] = useState<SessionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [enrollment, setEnrollment] = useState<{
    secretBase32: string
    otpauthUrl: string
    recoveryCodes: string[]
  } | null>(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [disablePass, setDisablePass] = useState('')
  const [disableCode, setDisableCode] = useState('')

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [m, s] = await Promise.all([
        api.get<MfaStatus>('/me/mfa'),
        api.get<{ items: SessionItem[]; currentSessionId: string | null }>('/me/sessions'),
      ])
      setMfa(m)
      setSessions(s.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('security.errors.load'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void reload()
  }, [reload])

  const onEnroll = async () => {
    setError(null)
    try {
      const r = await api.post<{
        secretBase32: string
        otpauthUrl: string
        recoveryCodes: string[]
      }>('/me/mfa/enroll')
      setEnrollment(r)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('security.errors.enrollFailed'))
    }
  }

  const onVerify = async () => {
    if (!/^\d{6}$/.test(verifyCode.trim())) {
      setError(t('security.errors.sixDigits'))
      return
    }
    setVerifying(true)
    setError(null)
    try {
      await api.post('/me/mfa/verify', { code: verifyCode.trim() })
      setEnrollment(null)
      setVerifyCode('')
      await reload()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? t('security.errors.invalidCode'))
      } else {
        setError(t('security.errors.network'))
      }
    } finally {
      setVerifying(false)
    }
  }

  const onDisable = async () => {
    if (!disablePass || !disableCode) {
      setError(t('security.errors.disableMissing'))
      return
    }
    setError(null)
    try {
      await api.post('/me/mfa/disable', {
        password: disablePass,
        code: /^\d{6}$/.test(disableCode) ? disableCode : undefined,
        recoveryCode: !/^\d{6}$/.test(disableCode) ? disableCode : undefined,
      })
      setDisablePass('')
      setDisableCode('')
      await reload()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? t('security.errors.disableFailed'))
      } else {
        setError(t('security.errors.network'))
      }
    }
  }

  const onRevokeSession = async (id: string) => {
    if (
      !(await confirmDialog({ message: t('security.confirm.revokeSession'), tone: 'danger' }))
    )
      return
    try {
      await api.delete(`/me/sessions/${id}`)
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('security.errors.generic'))
    }
  }

  const onRevokeAllOthers = async () => {
    if (
      !(await confirmDialog({ message: t('security.confirm.revokeAllOthers'), tone: 'danger' }))
    )
      return
    try {
      await api.delete('/me/sessions')
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('security.errors.generic'))
    }
  }

  if (loading) {
    return (
      <div className="t-muted text-sm flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> {t('security.loading')}
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
          <ShieldCheck size={20} className="text-jr2-700 dark:text-jr2-300" />
          {t('security.title')}
        </h1>
        <p className="text-xs t-muted">
          {t('security.subtitle')}
        </p>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* === MFA === */}
      <section className="surface p-5 space-y-4">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="t-primary font-semibold flex items-center gap-2">
              <KeyRound size={16} className="text-jr2-700 dark:text-jr2-300" />
              {t('security.mfa.title')}
            </h2>
            <p className="text-xs t-muted mt-1">
              {t('security.mfa.compatibility')}
            </p>
          </div>
          {mfa?.enabled ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40">
              <Check size={12} /> {t('security.mfa.active')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40">
              <ShieldOff size={12} /> {t('security.mfa.inactive')}
            </span>
          )}
        </header>

        {!mfa?.enabled && !enrollment && (
          <button
            type="button"
            onClick={() => void onEnroll()}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2"
          >
            {t('security.mfa.enable')}
          </button>
        )}

        {enrollment && (
          <div className="space-y-3 rounded-lg border border-jr2-500/30 bg-[var(--bg-input-soft)] p-4">
            <p className="text-sm t-primary">
              {t('security.enroll.step1')}
            </p>
            <div className="flex items-start gap-4">
              <img
                alt={t('security.enroll.qrAlt')}
                className="rounded bg-white p-2 w-44 h-44"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(enrollment.otpauthUrl)}`}
              />
              <div className="flex-1 text-xs t-secondary space-y-2">
                <div>
                  <span className="t-muted">{t('security.enroll.manualKey')}</span>
                  <pre className="mt-1 p-2 bg-slate-950/60 rounded font-mono text-jr2-700 dark:text-jr2-200 select-all break-all whitespace-pre-wrap">
                    {enrollment.secretBase32}
                  </pre>
                </div>
                <p className="t-muted">
                  {t('security.enroll.account')} <span className="t-primary">{auth.status === 'authenticated' ? auth.user.email : ''}</span>
                </p>
              </div>
            </div>

            <div className="border-t border-base/40 pt-3 space-y-2">
              <p className="text-sm t-primary">
                {t('security.enroll.step2')}
              </p>
              <ul className="grid grid-cols-2 gap-2 text-xs font-mono">
                {enrollment.recoveryCodes.map((c) => (
                  <li
                    key={c}
                    className="px-2 py-1 rounded bg-slate-950/60 border border-base/40 text-jr2-700 dark:text-jr2-200 select-all"
                  >
                    {c}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-amber-700 dark:text-amber-300">
                {t('security.enroll.codesWarning')}
              </p>
            </div>

            <div className="border-t border-base/40 pt-3 space-y-2">
              <p className="text-sm t-primary">
                {t('security.enroll.step3')}
              </p>
              <div className="flex gap-2">
                <input
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  placeholder="123456"
                  inputMode="numeric"
                  maxLength={8}
                  className="px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary font-mono text-center tracking-widest w-40 focus:outline-none focus:border-jr2-500/50"
                />
                <button
                  type="button"
                  onClick={() => void onVerify()}
                  disabled={verifying}
                  className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 flex items-center gap-2"
                >
                  {verifying && <Loader2 size={14} className="animate-spin" />}
                  {t('security.enroll.confirmActivate')}
                </button>
              </div>
            </div>
          </div>
        )}

        {mfa?.enabled && (
          <div className="space-y-3">
            <p className="text-xs t-muted">
              {t('security.mfa.recoveryRemaining')}{' '}
              <span className={mfa.recoveryCodesRemaining < 3 ? 'text-amber-700 dark:text-amber-300' : 't-primary'}>
                {mfa.recoveryCodesRemaining}
              </span>
              {mfa.lastVerifiedAt && (
                <>
                  {' · '}{t('security.mfa.lastVerified')}{' '}
                  {new Date(mfa.lastVerifiedAt).toLocaleString(currentLocaleTag())}
                </>
              )}
            </p>
            <details className="rounded-lg border border-base/40 p-3">
              <summary className="cursor-pointer text-sm t-secondary hover:text-rose-700 dark:hover:text-rose-300">
                {t('security.mfa.disable')}
              </summary>
              <div className="mt-3 space-y-2">
                <input
                  type="password"
                  value={disablePass}
                  onChange={(e) => setDisablePass(e.target.value)}
                  placeholder={t('security.mfa.currentPassword')}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:border-jr2-500/50"
                />
                <input
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  placeholder={t('security.mfa.totpOrRecovery')}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary font-mono focus:outline-none focus:border-jr2-500/50"
                />
                <button
                  type="button"
                  onClick={() => void onDisable()}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold text-rose-700 dark:text-rose-200 bg-rose-500/15 border border-rose-500/30 hover:bg-rose-500/25"
                >
                  {t('security.mfa.disable')}
                </button>
              </div>
            </details>
          </div>
        )}
      </section>

      {/* === Sesiones activas === */}
      <section className="surface p-5 space-y-3">
        <header className="flex items-center justify-between">
          <h2 className="t-primary font-semibold flex items-center gap-2">
            <Monitor size={16} className="text-jr2-700 dark:text-jr2-300" />
            {t('security.sessions.title')}
          </h2>
          {sessions.length > 1 && (
            <button
              type="button"
              onClick={() => void onRevokeAllOthers()}
              className="text-xs px-2.5 py-1 rounded border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/10"
            >
              {t('security.sessions.revokeAllOthers')}
            </button>
          )}
        </header>
        {sessions.length === 0 ? (
          <p className="text-xs t-faint">{t('security.sessions.empty')}</p>
        ) : (
          <ul className="divide-y divide-base/40">
            {sessions.map((s) => (
              <li key={s.id} className="py-2 flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="t-primary truncate">
                    {s.userAgent ?? t('security.sessions.unknownClient')}{' '}
                    {s.isCurrent && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40">
                        {t('security.sessions.current')}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] t-faint flex flex-wrap gap-2">
                    <span>{t('security.sessions.created')} {new Date(s.createdAt).toLocaleString(currentLocaleTag())}</span>
                    <span>· {t('security.sessions.lastActivity')} {new Date(s.lastSeenAt).toLocaleString(currentLocaleTag())}</span>
                    <span>· {t('security.sessions.ip')} {s.ipLastSeen ?? s.ipCreated ?? '—'}</span>
                  </div>
                </div>
                {!s.isCurrent && (
                  <button
                    type="button"
                    onClick={() => void onRevokeSession(s.id)}
                    className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                    title={t('security.sessions.revoke')}
                  >
                    <X size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
